var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions, DialogContent, Stack, Typography, styled } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import yup from '../../config/yup';
import { PlanType } from '../../enum';
import { StyledCloseIcon, StyledDialogTitle, StyledIconButton } from '../actual/exportDialog';
import { useTerminologyTranslation } from '../app/terminology';
import { Autocomplete, FormField, QueueCheckboxGroup, StyledDatePicker, UnControlledFormField, getLOBName, getUnassignedLOB, sortByLOBName, } from '../common';
import { selectDailyFilterQueues } from '../daily/selectors';
import { StyledDialog } from '../manageUsers';
import { QueueType } from '../multiskill/types';
import { selectTimelineOptions } from '../wizard/selectors';
import { useReportAPI } from './selectors';
var MAX_QUEUES = 5;
var EXPORT_DATE_FORMAT = 'YYYY-MM-DD';
var StyledFieldGroup = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        marginBottom: theme.spacing(5),
    });
});
var StyledDialogContent = styled(DialogContent)(function () { return ({
    borderBottom: 'none',
}); });
var StyledDialogActions = styled(DialogActions)(function () { return ({
    padding: '1.5rem',
    justifyContent: 'space-between',
}); });
export var forecastExportSchema = yup.object({
    planId: yup.number().integer().required(),
    scenarioKey: yup.string().optional(),
    queueIds: yup.array().of(yup.number().integer().required()).min(1).max(MAX_QUEUES).required(),
    viewType: yup.string().oneOf(['daily', 'interval']).required(),
    startPeriod: yup.number().integer().min(0).required(),
    endPeriod: yup
        .number()
        .integer()
        .min(0)
        .required()
        .test('period-order', 'endDateInvalid', function validatePeriodOrder(value) {
        return value >= this.parent.startPeriod;
    }),
    exportDate: yup
        .mixed()
        .nullable()
        .when('viewType', function (_a, schema) {
        var viewType = _a[0];
        return viewType === 'interval'
            ? schema.required().test('valid-date', 'invalid', function (value) { return !!(value === null || value === void 0 ? void 0 : value.isValid()); })
            : schema.nullable();
    })
        .defined(),
});
var getScenarioDateRange = function (scenario) {
    if (!(scenario === null || scenario === void 0 ? void 0 : scenario.startDate) || !scenario.periodLength || !scenario.planType)
        return null;
    var start = moment.utc(scenario.startDate).startOf('day');
    var end = start
        .clone()
        .add(scenario.periodLength, scenario.planType === PlanType.Weekly ? 'weeks' : 'months')
        .subtract(1, 'day');
    return { start: start, end: end };
};
var getDefaultExportDate = function (scenario, activeDate) {
    var dateRange = getScenarioDateRange(scenario);
    if (!dateRange)
        return null;
    var date = activeDate ? moment.utc(activeDate, EXPORT_DATE_FORMAT, true) : null;
    return (date === null || date === void 0 ? void 0 : date.isValid()) && date.isBetween(dateRange.start, dateRange.end, 'day', '[]')
        ? date
        : dateRange.start.clone();
};
var toQueueOptions = function (queues, planId) {
    var unassigned = getUnassignedLOB();
    // Export multiskill is triggered by selecting a member queue (`hasMultiskill`);
    // the group queue itself is a view-only row and must not be exported directly.
    return selectDailyFilterQueues(queues, planId)
        .filter(function (queue) { return queue.queueType !== QueueType.Multiskill; })
        .map(function (queue) { return ({
        uniqueId: queue.id,
        name: queue.name,
        lobName: getLOBName(queue.lobName, queue.subLobName, unassigned),
        hasMultiskill: Boolean(queue.hasMultiskill),
    }); })
        .sort(function (left, right) { return sortByLOBName(left, right, unassigned); });
};
export var ForecastExportDialog = function (_a) {
    var _b;
    var open = _a.open, viewType = _a.viewType, activeDate = _a.activeDate, defaultPlanId = _a.defaultPlanId, queuesState = _a.queuesState, onClose = _a.onClose, onSubmit = _a.onSubmit;
    var t = useTerminologyTranslation('report', { keyPrefix: 'daily.exportDialog' }).t;
    var translate = useTerminologyTranslation('translation').t;
    var report = useReportAPI()[0].data;
    var scenarios = useMemo(function () { var _a; return (_a = report === null || report === void 0 ? void 0 : report.scenarios) !== null && _a !== void 0 ? _a : []; }, [report === null || report === void 0 ? void 0 : report.scenarios]);
    var scenarioOptions = useMemo(function () {
        return scenarios.map(function (scenario) {
            var name = scenario.isBaseline ? translate('baseline') : scenario.name;
            return {
                uniqueId: "".concat(scenario.id),
                planId: scenario.id,
                name: name,
            };
        });
    }, [scenarios, translate]);
    var resolvedDefaultPlanId = scenarios.some(function (scenario) { return scenario.id === defaultPlanId; })
        ? defaultPlanId
        : (_b = scenarios[0]) === null || _b === void 0 ? void 0 : _b.id;
    var defaultScenario = scenarios.find(function (scenario) { return scenario.id === resolvedDefaultPlanId; });
    var defaultTimelines = useMemo(function () { return selectTimelineOptions(defaultScenario); }, [defaultScenario]);
    var initialValues = useMemo(function () { return ({
        planId: resolvedDefaultPlanId !== null && resolvedDefaultPlanId !== void 0 ? resolvedDefaultPlanId : 0,
        scenarioKey: "".concat(resolvedDefaultPlanId !== null && resolvedDefaultPlanId !== void 0 ? resolvedDefaultPlanId : 0),
        queueIds: [],
        viewType: viewType,
        startPeriod: 0,
        endPeriod: Math.max(defaultTimelines.length - 1, 0),
        exportDate: getDefaultExportDate(defaultScenario, activeDate),
    }); }, [activeDate, defaultScenario, defaultTimelines.length, resolvedDefaultPlanId, viewType]);
    var methods = useForm({
        defaultValues: initialValues,
        mode: 'onChange',
        resolver: yupResolver(forecastExportSchema),
    });
    var control = methods.control, handleSubmit = methods.handleSubmit, reset = methods.reset, setValue = methods.setValue;
    var planId = useWatch({ control: control, name: 'planId' });
    var queueIds = useWatch({ control: control, name: 'queueIds' });
    var startPeriod = useWatch({ control: control, name: 'startPeriod' });
    var endPeriod = useWatch({ control: control, name: 'endPeriod' });
    var exportDate = useWatch({ control: control, name: 'exportDate' });
    var selectedScenario = scenarios.find(function (scenario) { return scenario.id === planId; });
    var timelines = useMemo(function () { return selectTimelineOptions(selectedScenario); }, [selectedScenario]);
    var dateRange = useMemo(function () { return getScenarioDateRange(selectedScenario); }, [selectedScenario]);
    var queueOptions = useMemo(function () { return toQueueOptions(queuesState.data, planId); }, [planId, queuesState.data]);
    var validExportDate = !!dateRange && !!(exportDate === null || exportDate === void 0 ? void 0 : exportDate.isValid()) && exportDate.isBetween(dateRange.start, dateRange.end, 'day', '[]');
    var validPeriods = timelines.length > 0 && startPeriod >= 0 && endPeriod >= startPeriod && endPeriod < timelines.length;
    var canSubmit = planId > 0 &&
        queueIds.length > 0 &&
        queueIds.length <= MAX_QUEUES &&
        (viewType === 'interval' ? validExportDate : validPeriods);
    var wasOpen = useRef(open);
    var initializedDefaultPlanId = useRef(resolvedDefaultPlanId);
    useEffect(function () {
        var defaultChanged = initializedDefaultPlanId.current !== resolvedDefaultPlanId;
        if (open && (!wasOpen.current || defaultChanged)) {
            reset(initialValues);
            initializedDefaultPlanId.current = resolvedDefaultPlanId;
        }
        wasOpen.current = open;
    }, [initialValues, open, reset, resolvedDefaultPlanId]);
    useEffect(function () {
        if (queuesState.requesting || !queuesState.data)
            return;
        var validQueueIds = new Set(queueOptions.map(function (queue) { return queue.uniqueId; }));
        var nextQueueIds = queueIds.filter(function (queueId) { return validQueueIds.has(queueId); });
        if (nextQueueIds.length !== queueIds.length) {
            setValue('queueIds', nextQueueIds, { shouldValidate: true });
        }
    }, [queueIds, queueOptions, queuesState.data, queuesState.requesting, setValue]);
    var handleScenarioChange = useCallback(function (nextScenarioKey) {
        var nextScenarioOption = scenarioOptions.find(function (option) { return option.uniqueId === nextScenarioKey; });
        if (!nextScenarioOption)
            return;
        var nextPlanId = nextScenarioOption.planId;
        var nextScenario = scenarios.find(function (scenario) { return scenario.id === nextPlanId; });
        setValue('planId', nextPlanId, { shouldValidate: true });
        var nextTimelines = selectTimelineOptions(nextScenario);
        setValue('queueIds', [], { shouldValidate: true });
        setValue('startPeriod', 0, { shouldValidate: true });
        setValue('endPeriod', Math.max(nextTimelines.length - 1, 0), { shouldValidate: true });
        setValue('exportDate', getDefaultExportDate(nextScenario, activeDate), { shouldValidate: true });
    }, [activeDate, scenarioOptions, scenarios, setValue]);
    var handleStartPeriodChange = useCallback(function (value) {
        if (value > endPeriod)
            setValue('endPeriod', value, { shouldValidate: true });
    }, [endPeriod, setValue]);
    var handleEndPeriodChange = useCallback(function (value) {
        if (value < startPeriod)
            setValue('startPeriod', value, { shouldValidate: true });
    }, [setValue, startPeriod]);
    var handleClose = useCallback(function () {
        reset(initialValues);
        onClose();
    }, [initialValues, onClose, reset]);
    var handleFormSubmit = function (data) {
        var _a;
        var selectionBase = { planId: data.planId, queueIds: data.queueIds };
        if (viewType === 'interval') {
            if (!((_a = data.exportDate) === null || _a === void 0 ? void 0 : _a.isValid()))
                return;
            onSubmit(__assign(__assign({}, selectionBase), { viewType: viewType, exportDate: data.exportDate.format(EXPORT_DATE_FORMAT) }));
        }
        else {
            onSubmit(__assign(__assign({}, selectionBase), { viewType: viewType, startPeriod: data.startPeriod, endPeriod: data.endPeriod }));
        }
        handleClose();
    };
    return (_jsx(FormProvider, __assign({}, methods, { children: _jsx(StyledDialog, __assign({ open: open, onClose: handleClose, "aria-labelledby": "forecast-export-title", fullWidth: true, maxWidth: "md" }, { children: _jsxs(Box, __assign({ component: "form", onSubmit: handleSubmit(handleFormSubmit) }, { children: [_jsx(StyledDialogTitle, __assign({ id: "forecast-export-title" }, { children: t(viewType === 'interval' ? 'intervalTitle' : 'title') })), _jsx(StyledIconButton, __assign({ "aria-label": t('close'), onClick: handleClose }, { children: _jsx(StyledCloseIcon, { icon: faXmark }) })), _jsxs(StyledDialogContent, __assign({ dividers: true }, { children: [_jsx(StyledFieldGroup, { children: _jsx(FormField, __assign({ name: "scenarioKey", label: t('scenario'), control: control }, { children: _jsx(Autocomplete, { id: "forecast-export-scenario", "aria-label": t('scenario'), disablePortal: true, options: scenarioOptions, getOptionValue: function (option) { return option.uniqueId; }, onChange: handleScenarioChange }) })) }), _jsx(StyledFieldGroup, { children: _jsx(Controller, { name: "queueIds", control: control, render: function (_a) {
                                        var field = _a.field;
                                        return (_jsx(UnControlledFormField, __assign({ label: t('queueLimitSelect') }, { children: _jsx(Box, __assign({ position: "relative", width: "100%" }, { children: _jsx(QueueCheckboxGroup, { options: queueOptions, value: field.value, onChange: field.onChange, maxSelected: MAX_QUEUES, labels: { search: t('queueSearch') }, ariaLabel: t('queueLimitSelect'), disabled: queuesState.requesting }) })) })));
                                    } }) }), viewType === 'interval' ? (_jsx(FormField, __assign({ name: "exportDate", label: t('intervalDate'), control: control }, { children: _jsx(StyledDatePicker, { "aria-label": t('intervalDate'), control: control, minDate: dateRange === null || dateRange === void 0 ? void 0 : dateRange.start, maxDate: dateRange === null || dateRange === void 0 ? void 0 : dateRange.end, format: translate('dateFormat.short') }) }))) : (_jsx(UnControlledFormField, __assign({ label: t('date') }, { children: _jsxs(Stack, __assign({ direction: "row", alignItems: "center", gap: 2, flex: 1 }, { children: [_jsx(FormField, __assign({ name: "startPeriod", control: control }, { children: _jsx(Autocomplete, { id: "forecast-export-start-period", "aria-label": t('startDate'), sx: { minWidth: '11rem' }, options: timelines, getOptionValue: function (option) { return option.id; }, onChange: handleStartPeriodChange }) })), _jsx(Typography, __assign({ fontSize: "0.875rem" }, { children: t('to') })), _jsx(FormField, __assign({ name: "endPeriod", control: control }, { children: _jsx(Autocomplete, { id: "forecast-export-end-period", "aria-label": t('endDate'), sx: { minWidth: '11rem' }, options: timelines, getOptionValue: function (option) { return option.id; }, onChange: handleEndPeriodChange }) }))] })) })))] })), _jsxs(StyledDialogActions, { children: [_jsx(Button, __assign({ onClick: handleClose, variant: "outlined", sx: { minWidth: '6rem' } }, { children: t('cancel') })), _jsx(Button, __assign({ type: "submit", variant: "contained", disabled: !canSubmit || queuesState.requesting, sx: { minWidth: '6.375rem' } }, { children: t('export') }))] })] })) })) })));
};
